import * as React from 'react';
import styled from 'styled-components';
import { UserNotification, UserToken } from '../../amorg-common';
import { getUserNotifications } from '../../api/notificationClientApi';
import { getMaybeActiveUserOrRefresh } from '../../api/serverApiHelpers';
import { tablet } from '../../designSystem/BreakPoints';
import { Card } from '../../designSystem/Card';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { DeviceView } from '../../designSystem/DeviceView';
import { Icon } from '../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { P } from '../../designSystem/typography/Body';
import { H3 } from '../../designSystem/typography/Heading';
import { toTextDateString } from '../../utils/date';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import { ActivityTimeLine } from './_components/ActivityTimeline';
import { DashboardCard } from './_components/DashboardCard';
import { DashboardFactCard } from './_components/DashboardFactCard';
import DashboardComponents from './DashboardComponents';


export default function DashboardPage(): JSX.Element {
    return (
        <MemberPageWrapper selectedMenuItemKey='dashboard' breadCrumbs={[{ text: 'Overblik', url: '' }]}>
            <DashboardPageContent />
        </MemberPageWrapper>
    );
}

function DashboardPageContent(): JSX.Element {

    const [startDate,] = React.useState(new Date());
    const [user, setUser] = React.useState<UserToken | null>(null);

    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewNotifications']);

    React.useState(()=>{
        const getData = async ()=>{
            const usr = await getMaybeActiveUserOrRefresh();
            if(usr.hasAccess)
                setUser(usr.tokenUser);
        };
        getData();
    })

    const sharedTimeline = 
        <TimelineDashGrid>
            <Card variant="outlined">
                <ActivityTimeLine yearMonthStart={startDate} />
            </Card>
        </TimelineDashGrid>


    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        <>
            {user && 
            <>
                <PageHeader headerIcon='dashboard' headerText="Overblik" headerStyle='secondary-gray' />
                <SingleColumnLayout>
                <DashboardLayout>
                    <DeviceView
                        tablet={sharedTimeline}
                        desktop={sharedTimeline} />

                    <CallToActionGrid>
                        <NotificationCard />
                        <DashboardFactCard />
                    </CallToActionGrid>
                    
                    <FeatureTitle>
                        <PageSection
                            header={{
                                title: "Dine moduler",
                                subTitle:{
                                    text:`Status pr. ${toTextDateString(new Date())}`,
                                },
                            }}
                            headerStyleVariant="minimal"
                            contentStyleVariant="minimal"
                            contentPadding="16px 0"
                            
                        >
                        <FeatureGrid>
                            <DashboardComponents />
                        </FeatureGrid>

                        </PageSection>
                        
                    </FeatureTitle>
                    
                    
                </DashboardLayout>
            </SingleColumnLayout>
            </>
            }
        </>
    )
}


function NotificationCard(): JSX.Element {

    const [notifications, setNotifications] = React.useState<UserNotification[]>([]);

    React.useEffect(()=>{
            const getData = async ()=>{
                const user = await getMaybeActiveUserOrRefresh([]);
                if(!user.tokenUser) return;
                const notifs = await getUserNotifications(user.tokenUser.userId, false, false);
                setNotifications(notifs);
            };
            getData();
        }, []);



    return (
        <DashboardCard shadow redirectTo={"/notifications"}>
            <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                <H3>Notifikationer</H3>
                <Icon name="interface_actions_notificationOn" color="black" />
            </Flex>
            <P>Der ligger {notifications.length} notifikation{notifications.length !== 1 ? "er" : ""}</P>
        </DashboardCard>
    );
}


const DashboardLayout = styled.div`
    display: grid;
    grid-template-areas:
        'timeline timeline callToAction'
        'featureTitle featureTitle featureTitle'
        'feature feature feature';
    
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto 1fr;
    gap: 24px;

    @media(max-width: ${tablet}px){
        grid-template-columns: 1fr;
        grid-template-rows: auto auto 1fr;

        grid-template-areas:
            'timeline'
            'callToAction'
            'featureTitle'
            'feature'
        ;
    }
`;

const TimelineDashGrid = styled.div`
    display: grid;
    grid-area: timeline;
`;

const FeatureTitle = styled.div`
    
    grid-area: featureTitle;
    
`;

const FeatureGrid = styled.div`
    display: grid;
    grid-area: feature;
`;

const CallToActionGrid = styled.div`
    display: grid;
    grid-area: callToAction;
    grid-template-rows: min-content 1fr;
    gap: 24px;

    @media(max-width: ${tablet}px){
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr 2fr;
    }
`;